import React, { Component } from "react";
import {
  StyleSheet,
  Dimensions,
  View,
  Text,
  TouchableHighlight,
  Image,
  Platform,
  TouchableOpacity,
  Modal as ModalWeb,
} from "react-native";
import { connect } from "react-redux";
import * as ImagePicker from "expo-image-picker";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getApiPath } from "../../api";
import * as Permissions from "expo-permissions";
import colors from "../../constants/colors";
import NewIssue from "../dialogs/newIssue";
import Swal from "sweetalert2";
import { uploadFile } from "../../actions/submissions";

const IMAGE_WIDTHS = { mini: 134, small: 200, medium: 300, large: 480 };
const PICKER_OPTIONS = {
  mediaTypes: ImagePicker.MediaTypeOptions.Images,
  aspect: [4, 3],
  quality: 0.6,
};

const swalDefaults = {
  confirmButtonColor: colors.orange,
  backdrop: false,
};

function hasInitialValue(initial) {
  return initial && initial !== 0 && initial !== "0" && initial !== "";
}

function resolveImageSrc(initial) {
  if (!hasInitialValue(initial)) return null;
  if (typeof initial === "object") return initial.uri || null;

  const str = String(initial);
  if (/^(https?|blob|file):/.test(str) || str.includes("base64")) return str;
  return getApiPath() + str;
}

function getImageWidth(width) {
  return IMAGE_WIDTHS[width] || IMAGE_WIDTHS.large;
}

async function confirmRemove() {
  const { isConfirmed } = await Swal.fire({
    title: "Remove Picture",
    text: "Are you sure you would like to remove this picture?",
    confirmButtonText: "OK",
    cancelButtonText: "Cancel",
    showCancelButton: true,
    confirmButtonColor: colors.orange,
    backdrop: Platform.OS !== "web",
  });
  return isConfirmed;
}

class Picture extends Component {
  state = {
    imagePath: resolveImageSrc(this.props.initial),
    hasImage: hasInitialValue(this.props.initial),
    ignoreInitial: false,
    pictureText: this.props.placeholder,
    dialogVisible: false,
    showImageModal: false,
    modalImagePath: null,
  };

  getDisplaySrc() {
    const { imagePath, ignoreInitial } = this.state;
    if (ignoreInitial && imagePath) return imagePath;
    return resolveImageSrc(this.props.initial) || imagePath;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.initial === this.props.initial || this.state.ignoreInitial) return;
    this.setState({
      imagePath: resolveImageSrc(nextProps.initial),
      hasImage: hasInitialValue(nextProps.initial),
    });
  }

  setLocalPreview = (uri) => {
    const previewUri = Platform.OS === "ios" ? uri.replace("file://", "") : uri;
    this.setState({ imagePath: previewUri, hasImage: true, ignoreInitial: true });
  };

  onUploadSuccess = (path, onChangeValue) => {
    this.setState(
      {
        imagePath: resolveImageSrc(path),
        hasImage: true,
        ignoreInitial: false,
      },
      () => this.props.onChange(this.props.id, onChangeValue)
    );
  };

  pickFromLibrary = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      ...PICKER_OPTIONS,
      allowsEditing: true,
      base64: false,
    });
    const asset = result.canceled ? null : result.assets?.[0];
    if (asset?.uri) this.handlePickedImage(asset.uri, asset.type);
  };

  takePhoto = async () => {
    const result = await ImagePicker.launchCameraAsync({
      ...PICKER_OPTIONS,
      allowEditing: false,
    });
    const asset = result.canceled ? null : result.assets?.[0];
    if (asset?.uri) this.handlePickedImage(asset.uri, asset.type);
  };

  handlePickedImage = (uri, type) => {
    this.setLocalPreview(uri);
    this.uploadImage(uri, type);
  };

  showSourcePicker = () => {
    Swal.fire({
      text: "Where do you want to pick your image?",
      confirmButtonText: "Take a picture",
      denyButtonText: "Choose from gallery",
      cancelButtonText: "Cancel",
      showDenyButton: true,
      showCancelButton: true,
      ...swalDefaults,
    }).then((result) => {
      if (result.isConfirmed) this.requestPermission(Permissions.CAMERA, this.takePhoto, this.pickFromLibrary);
      else if (result.isDenied) this.requestPermission(Permissions.CAMERA_ROLL, this.pickFromLibrary);
    });
  };

  requestPermission = async (permission, onGranted, onDenied) => {
    const { status } = await Permissions.askAsync(permission);
    if (status === "granted") {
      onGranted();
      return;
    }

    const isCamera = permission === Permissions.CAMERA;
    const { isConfirmed, isDenied } = await Swal.fire({
      title: "We need your permission",
      text: isCamera
        ? "Please grant access to the camera in order to continue"
        : "Please grant access to the gallery so you can continue",
      confirmButtonText: isCamera ? "Take a picture" : "Accept",
      denyButtonText: isCamera ? "Choose from gallery" : undefined,
      cancelButtonText: "Cancel",
      showDenyButton: isCamera,
      showCancelButton: true,
      ...swalDefaults,
    });

    if (isConfirmed) {
      const retry = await Permissions.askAsync(permission);
      if (retry.status === "granted") onGranted();
    } else if (isDenied && onDenied) {
      onDenied();
    }
  };

  uploadImage = (uri, data) => {
    if (Platform.OS === "web" && uri.substr(5, 6) !== "image/") {
      Swal.fire({
        text: "Please select only images.",
        confirmButtonText: "OK",
        ...swalDefaults,
      });
      return;
    }

    this.props.uploadFile(uri, (path) => {
      const value = Platform.OS === "web" ? path : { data, uri: path };
      this.onUploadSuccess(path, value);
    });
  };

  handleAddPress = () => {
    if (this.props.resetDropDown) this.props.resetDropDown();
    if (Platform.OS === "web") this.pickFromLibrary();
    else this.showSourcePicker();
  };

  removePicture = async () => {
    if (!(await confirmRemove())) return;
    this.setState({
      imagePath: null,
      hasImage: false,
      ignoreInitial: true,
      pictureText: "No picture added",
    });
    this.props.onChange(this.props.id, 0);
  };

  renderImagePreview(displaySrc) {
    const { width } = this.props;
    const imageWidth = getImageWidth(width);
    const onPress = () => displaySrc && this.setState({ showImageModal: true, modalImagePath: displaySrc });

    if (Platform.OS === "web") {
      return (
        <TouchableOpacity onPress={onPress}>
          <img src={displaySrc} style={{ width: imageWidth, height: "auto" }} />
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity onPress={onPress}>
        <Image
          source={{ uri: displaySrc }}
          style={{
            aspectRatio: 1,
            width: Dimensions.get("window").width - 80,
            height: 320,
          }}
        />
      </TouchableOpacity>
    );
  }

  render() {
    const { showImageModal, modalImagePath, hasImage, pictureText, dialogVisible } = this.state;
    const {
      label,
      isIssuable,
      onSubmitIssue,
      id,
      isCompleted,
      userRole,
      width,
      openIssue,
      defaultComponent,
    } = this.props;
    const displaySrc = this.getDisplaySrc();

    return (
      <>
        {!width && (
          <NewIssue
            visible={dialogVisible}
            id={id}
            label={label}
            onSubmitIssue={onSubmitIssue}
            handleClose={() => this.setState({ dialogVisible: false })}
            width={width}
          />
        )}

        <View style={styles.container}>
          <Text style={styles.label2}>{label}</Text>

          {isIssuable && isCompleted && userRole !== 'Auditor' && (
            <TouchableOpacity
              style={styles.issueButton}
              onPress={() => (width ? openIssue() : this.setState({ dialogVisible: true }))}
            >
              <Text style={styles.issueText}>Issue</Text>
            </TouchableOpacity>
          )}

          {showImageModal && (
            <ModalWeb animationType="slide" transparent visible={showImageModal}>
              <View style={styles.modalImgContainer}>
                <Text style={styles.closeImgModal} onPress={() => this.setState({ showImageModal: false, modalImagePath: null })}>
                  X
                </Text>
                <img src={modalImagePath} style={{ maxHeight: "100%", maxWidth: "100%" }} />
              </View>
            </ModalWeb>
          )}

          {hasImage ? (
            <View style={styles.previewRow}>
              <View style={styles.wrapperUploaded}>{this.renderImagePreview(displaySrc)}</View>
              {!defaultComponent && (
                <TouchableHighlight style={styles.deleteButton} onPress={this.removePicture}>
                  <MaterialCommunityIcons name="delete" size={28} color="red" />
                </TouchableHighlight>
              )}
            </View>
          ) : (
            <View style={styles.wrapperInitial}>
              <TouchableHighlight
                disabled={defaultComponent}
                style={styles.button}
                underlayColor={colors.secondary}
                onPress={this.handleAddPress}
              >
                <Text style={styles.buttonText}>Add picture</Text>
              </TouchableHighlight>
              <Text style={styles.label}>{pictureText}</Text>
            </View>
          )}
        </View>
      </>
    );
  }
}

export default connect((state) => ({
  userRole: state.user.get("userRole"),
}), (dispatch) => ({
  uploadFile: (uri, callback) => dispatch(uploadFile(null, uri, ".png", callback)),
}))(Picture);

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    paddingTop: 5,
  },
  wrapperInitial: {
    flexDirection: "row",
    alignItems: "center",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#C8C9CB",
    borderRadius: 5,
  },
  wrapperUploaded: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#C8C9CB",
    borderRadius: 5,
  },
  previewRow: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  button: {
    backgroundColor: colors.primary,
    borderRadius: 5,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    overflow: "hidden",
  },
  buttonText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "300",
    fontFamily: "Roboto",
  },
  label: {
    fontSize: 16,
    fontWeight: "300",
    marginLeft: 10,
    fontFamily: "Roboto",
    fontStyle: "italic",
    color: "#424242",
    wordBreak: "break-all",
  },
  label2: {
    fontSize: 20,
    color: "#939598",
    fontWeight: "300",
    fontFamily: "Roboto",
  },
  issueButton: {
    paddingBottom: 10,
    paddingTop: 5,
  },
  issueText: {
    color: colors.primary,
    fontWeight: "bold",
  },
  deleteButton: {
    position: "absolute",
    top: 20,
    right: 20,
    backgroundColor: "white",
    height: 40,
    width: 40,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 20,
  },
  modalImgContainer: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    position: "relative",
    backgroundColor: "rgba(0,0,0,0.4)",
    overflow: "hidden",
    justifyContent: "center",
  },
  closeImgModal: {
    position: "absolute",
    top: 10,
    right: 20,
    fontSize: 22,
    color: "white",
  },
});
